import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, ApiError } from './api';

export type AuthMode = 'mock' | 'entra';
export type AppEnv = 'local' | 'acceptance' | 'production';

// The VHT role vocabulary (for the mock role picker). Mirrors the API UserRole enum.
export const MOCK_ROLES = [
  'admin',
  'planner',
  'werkvoorbereider',
  'administratie',
  'logistiek',
] as const;

const fallbackMode: AuthMode =
  (import.meta.env.VITE_AUTH_MODE as AuthMode | undefined) ??
  (import.meta.env.DEV ? 'mock' : 'entra');

let resolvedMode: AuthMode | null = null;
let resolvedAppEnv: AppEnv | null = null;

/** Resolve the auth mode + env badge from the API before first paint; falls back on failure. */
export async function loadAuthConfig(): Promise<AuthMode> {
  try {
    const timeout = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('auth config timeout')), 3000),
    );
    const cfg = await Promise.race([
      apiClient.get<{ mode: AuthMode; appEnv?: AppEnv }>('/api/v1/auth/config'),
      timeout,
    ]);
    resolvedMode = cfg.mode === 'mock' || cfg.mode === 'entra' ? cfg.mode : fallbackMode;
    resolvedAppEnv = cfg.appEnv ?? null;
  } catch {
    resolvedMode = fallbackMode;
  }
  return resolvedMode;
}

export function authMode(): AuthMode {
  return resolvedMode ?? fallbackMode;
}
export function appEnv(): AppEnv {
  return resolvedAppEnv ?? (import.meta.env.VITE_APP_ENV as AppEnv) ?? 'local';
}

export interface SessionPrincipal {
  userId: string;
  email: string;
  displayName?: string;
  roles: string[];
  /** What this user may do (Beheer › Rechten; admin: everything). See `@/lib/access`. */
  permissions: string[];
  /** Roles with work of their own — presentation only (dashed role chips). */
  rolesWithWork: string[];
  userType: 'internal';
  isMock: boolean;
}

const MOCK_KEY = 'vht-mock-session';
export interface MockSession {
  email: string;
  roles: string[];
}
export function getMockSession(): MockSession | null {
  if (authMode() !== 'mock') return null;
  try {
    const raw = localStorage.getItem(MOCK_KEY);
    return raw ? (JSON.parse(raw) as MockSession) : null;
  } catch {
    return null;
  }
}
export function setMockSession(s: MockSession): void {
  localStorage.setItem(MOCK_KEY, JSON.stringify(s));
}
export function clearMockSession(): void {
  localStorage.removeItem(MOCK_KEY);
}

function displayNameFromEmail(email: string): string {
  const local = email.split('@')[0] ?? email;
  return local
    .split(/[._-]/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
}

export const sessionQueryKey = ['session'] as const;

export async function fetchSession(): Promise<SessionPrincipal | null> {
  if (authMode() === 'mock') {
    const mock = getMockSession();
    if (!mock) return null;
    // The roles come from the picker, the rights from the API: the matrix lives in
    // the database, also locally (the api client sends the mock headers along).
    const me = await apiClient.get<SessionPrincipal>('/api/v1/auth/me');
    return { ...me, userId: 'mock-user', displayName: displayNameFromEmail(mock.email) };
  }
  try {
    return await apiClient.get<SessionPrincipal>('/api/v1/auth/me');
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) return null;
    throw err;
  }
}

export function useSession() {
  return useQuery({ queryKey: sessionQueryKey, queryFn: fetchSession, staleTime: 5 * 60_000 });
}

export function hasAnyRole(session: SessionPrincipal | null | undefined, roles: string[]): boolean {
  return !!session?.roles.some((r) => roles.includes(r));
}

/** Begin the Entra SSO flow (BFF): the API handles the OAuth dance. */
export function login(redirect?: string): void {
  const q = redirect ? `?redirect=${encodeURIComponent(redirect)}` : '';
  window.location.href = `/api/v1/auth/login${q}`;
}

export function useLogout() {
  const qc = useQueryClient();
  return async () => {
    if (authMode() === 'mock') {
      clearMockSession();
      await qc.invalidateQueries({ queryKey: sessionQueryKey });
      return;
    }
    try {
      await apiClient.post('/api/v1/auth/logout', {});
    } finally {
      window.location.href = '/login';
    }
  };
}
